import { ThemeProvider, StyledEngineProvider, responsiveFontSizes, createTheme } from '@mui/material/styles';
import APIContextProvider from './components/Contexts/APIContext';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@mui/material/CssBaseline';
import { Navigate, Route, Routes } from 'react-router-dom';
import MsalAuthProvider from './components/Contexts/MsalContext';
import { msalInstance } from './msalConfig';
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import Home from './components/Home';
import IndexContainer from './components/SearchIndex/IndexContainer';
import TagList from './components/Tags/TagList';
import React from 'react';
import IssueContainer from './components/Issue/IssueContainer';
import NavBar from './components/Navigation/NavBar'
import CallfinderNav from './components/Navigation/CallfinderNav';
import TeamLeaderFeedbackContainer from './components/Feedback/TeamLeader/TeamLeaderFeedbackContainer';
import AgentFeedbackContainer from './components/Feedback/Agent/AgentFeedbackContainer';
import QATeamLeaderFeedbackContainer from './components/Feedback/QATeamLeader/QATeamLeaderFeedbackContainer';
import AppProvider from './components/Contexts/AppContext';
import { InteractionType } from '@azure/msal-browser';
import ScorecardManagementContainer from './components/Scorecard Management/ScorecardManagementContainer';
import QAFeedbackContainer from './components/Feedback/QA/QAFeedbackContainer';
import AgentViewFeedbackContainer from './components/AgentView/AgentViewFeedbackContainer';
import FirestoneCallFinderTabContainer from './components/AgentView/FirestoneCallfinderTabContainer';
import TeamLeaderScorecardsContainer from './components/Feedback/TeamLeader/TeamLeaderScorecardsContainer';
import TeamLeaderQaiChallengeContainer from './components/QaiScorecardChallenge/TeamLeaderView/TeamLeaderQaiChallengeContainer';
import QaQaiChallengeContainer from './components/QaiScorecardChallenge/QaView/QaQaiChallengeContainer';
import ClientReportSelectionContainer from './components/Client Report Selection/ClientReportSelectionContainer';
import MemoContainer from './components/Memo/MemoContainer';
import ClientInsightsContainer from './components/ClientInsights/ClientInsightsContainer';
import CallContainer from './components/Monitoring/CallContainer';

declare module "@mui/material/styles/createPalette" {
    interface Palette {
        tertiary: {
            main: React.CSSProperties['color']
        }
    }
    interface PaletteOptions {
        tertiary: {
            main: React.CSSProperties['color']
        }
    }
}

let theme = responsiveFontSizes(createTheme({
    palette: {
        background: { default: '#fff' },
        primary: {
            main: '#1d8abf',
            light: '#B4C0C2',
            dark: '#545a86',
            contrastText: '#fff'
        },
        secondary: {
            main: '#3bb7b2',
            light: '#DDDDDD',
            dark: '#2e8593',
            contrastText: '#fff'
        },
        tertiary: { main: '#00AEEF' },
        warning: { main: '#f37360' },
        error: { main: '#F05B72' },
        success: { main: '#5EC4B6' }
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    padding: '8px'
                }
            },
            defaultProps: {
                square: true
            }
        }
    },
    typography: {
        fontFamily: 'Lexend'
    }
}));

export default function App() {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <MsalProvider instance={msalInstance}>
                    <MsalAuthProvider>
                        <SnackbarProvider>
                            <APIContextProvider>
                                <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
                                    <AppProvider>
                                        <Routes>
                                            <Route element={<NavBar />}>
                                                <Route path='Search' element={<CallfinderNav />}>
                                                    <Route path='Feedback' element={<TeamLeaderFeedbackContainer />} />
                                                    <Route path='TeamLeaderScorecards' element={<TeamLeaderScorecardsContainer />} />
                                                    <Route path='FeedbackChallenges' element={<QATeamLeaderFeedbackContainer />} />
                                                    <Route path='QAFeedback' element={<QAFeedbackContainer />} />
                                                    <Route path='SearchIndex' element={<IndexContainer readonly={false} defaultCustomerIDFilter={null} />} />
                                                    <Route path='TagManagement' element={<TagList />} />
                                                    <Route path='ClientInsights' element={<ClientInsightsContainer />} />
                                                    <Route path='ScorecardManagement' element={<ScorecardManagementContainer />} />
                                                    <Route path='ClientReportSelection' element={<ClientReportSelectionContainer />} />
                                                    <Route path='TeamLeaderQaiChallenge' element={<TeamLeaderQaiChallengeContainer />} />
                                                    <Route path='QaQaiChallenge' element={<QaQaiChallengeContainer />} />
                                                    <Route index element={<Home />} />
                                                </Route>
                                                <Route path='Call' element={<CallContainer />} />
                                                <Route path='AgentFeedback' element={<AgentFeedbackContainer />} />
                                                <Route path='AgentView' element={<AgentViewFeedbackContainer />} />
                                                <Route path='MemoView' element={<MemoContainer />} />
                                                <Route path='AgentView/Call' element={<FirestoneCallFinderTabContainer />} />
                                                <Route path='*' element={<Navigate to='Search' replace />} />
                                            </Route>
                                            <Route path='Issues' element={<IssueContainer />} />
                                        </Routes>
                                    </AppProvider>
                                </MsalAuthenticationTemplate>
                            </APIContextProvider>
                        </SnackbarProvider>
                    </MsalAuthProvider>
                </MsalProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}